/**
 * Bubble - one bubble, with the thing inside it.
 *
 * Drawn rather than imaged: an SVG sphere with a radial tint, a specular
 * highlight and a rim, so it holds up at any size on any screen and costs
 * nothing to load. The object itself sits on top and does not distort.
 *
 * The states are the game's grammar:
 *   idle     drifting, waiting to be named
 *   popping  named. Bursts.
 *   given    "want" - it goes to the child
 *   placed   "put"  - it goes into the basket
 *   gone     removed from the field
 */

import { getObject } from '../content/objects.js';

export default function Bubble({ bubble, highlighted, pointing, listening }) {
  const object = getObject(bubble.objectId);
  if (!object || bubble.state === 'gone') return null;

  return (
    <div
      className={[
        'bp-bubble',
        `bp-bubble--${bubble.state}`,
        highlighted ? 'is-highlighted' : '',
        pointing ? 'is-pointed' : '',
        listening ? 'is-listening' : '',
      ].filter(Boolean).join(' ')}
      style={{ '--tint': object.tint, '--drift': `${(bubble.slot % 3) * 0.9}s` }}
      data-object={object.id}
    >
      <svg className="bp-bubble__glass" viewBox="0 0 100 100" aria-hidden="true">
        <defs>
          <radialGradient id={`bp-fill-${object.id}`} cx="35%" cy="30%" r="75%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="45%" stopColor={object.tint} stopOpacity="0.42" />
            <stop offset="100%" stopColor={object.tint} stopOpacity="0.72" />
          </radialGradient>
          <radialGradient id={`bp-rim-${object.id}`} cx="50%" cy="50%" r="50%">
            <stop offset="88%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.8" />
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="47" fill={`url(#bp-fill-${object.id})`} />
        <circle cx="50" cy="50" r="47" fill={`url(#bp-rim-${object.id})`} />
        <ellipse cx="34" cy="28" rx="13" ry="9" fill="#ffffff" opacity="0.75" transform="rotate(-25 34 28)" />
        <ellipse cx="68" cy="70" rx="6" ry="4" fill="#ffffff" opacity="0.35" transform="rotate(-25 68 70)" />
      </svg>

      <span className="bp-bubble__art" role="img" aria-label={object.word}>{object.art}</span>

      {/* Droplets, drawn only while popping so they cost nothing at rest. */}
      {bubble.state === 'popping' && (
        <span className="bp-bubble__burst" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <span key={i} className={`bp-bubble__drop bp-bubble__drop--${i}`} />
          ))}
        </span>
      )}
    </div>
  );
}
